/**
 * site-config.ts — per-site configuration for the listing autofill extension (DC-3).
 *
 * Browser-safe (no dependencies): the single source of truth read by BOTH the API renderers
 * (apps/api/src/services/listing-extension/listing-renderers.ts) and the extension adapters,
 * so field placements, length limits and photo limits are never copied.
 */
export const LISTING_SITES = ['facebook_marketplace', 'craigslist'];
/** User-facing site names, shared by the API, the landlord app and the extension panel. */
export const SITE_LABELS = {
    facebook_marketplace: 'Facebook Marketplace',
    craigslist: 'Craigslist',
};
/** The DC-3 common listing field set, in table order. */
export const COMMON_FIELD_ITEMS = [
    'title',
    'description',
    'property_type',
    'bedrooms',
    'bathrooms',
    'square_feet',
    'rent',
    'security_deposit',
    'application_fee',
    'pet_fees',
    'other_move_in_costs',
    'parking_fee',
    'available_date',
    'lease_terms',
    'pets',
    'utilities_included',
    'laundry',
    'parking',
    'heating_cooling',
    'furnished',
    'smoking',
    'amenities',
    'contact_email',
    'address',
    'photos',
];
export const SITE_CONFIG = {
    facebook_marketplace: {
        titleMaxLength: null,
        descriptionMaxLength: 9999, // verify-live
        photoLimit: 50, // verify-live
        placement: {
            title: 'none',
            description: 'field',
            property_type: 'field',
            bedrooms: 'field',
            bathrooms: 'field',
            square_feet: 'field',
            rent: 'field',
            security_deposit: 'description',
            application_fee: 'description',
            pet_fees: 'description',
            other_move_in_costs: 'description',
            parking_fee: 'description',
            available_date: 'description', // Facebook's rental form has no date-available control
            lease_terms: 'description',
            pets: 'description', // Facebook's rental form has no pets control
            utilities_included: 'description',
            laundry: 'field',
            parking: 'field',
            heating_cooling: 'field',
            furnished: 'description',
            smoking: 'description',
            amenities: 'description',
            contact_email: 'none', // Facebook's rental form has no contact email control
            address: 'field',
            photos: 'field',
        },
    },
    craigslist: {
        titleMaxLength: 70, // verify-live
        descriptionMaxLength: 10000, // verify-live
        photoLimit: 24, // verify-live
        placement: {
            title: 'field',
            description: 'field',
            property_type: 'field',
            bedrooms: 'field',
            bathrooms: 'field',
            square_feet: 'field',
            rent: 'field',
            security_deposit: 'description',
            application_fee: 'description',
            pet_fees: 'description',
            other_move_in_costs: 'description',
            parking_fee: 'description',
            available_date: 'field',
            lease_terms: 'description',
            pets: 'field',
            utilities_included: 'description',
            laundry: 'field',
            parking: 'field',
            heating_cooling: 'description',
            furnished: 'field',
            smoking: 'field',
            amenities: 'description',
            contact_email: 'field',
            address: 'field',
            photos: 'field',
        },
    },
};
/**
 * The generic "other sites" rendering for the landlord Marketing tab (listing-marketing-hub BR-9).
 * Deliberately NOT a member of LISTING_SITES / SITE_CONFIG: it is never filled by the extension
 * and never recorded as an external post. Every item is written into the description as text,
 * except the title and the marketing description, which have their own slots.
 */
export const GENERIC_SITE_CONFIG = {
    titleMaxLength: 120,
    descriptionMaxLength: 8000,
    photoLimit: 20,
    placement: {
        title: 'field',
        description: 'field',
        property_type: 'description',
        bedrooms: 'description',
        bathrooms: 'description',
        square_feet: 'description',
        rent: 'description',
        security_deposit: 'description',
        application_fee: 'description',
        pet_fees: 'description',
        other_move_in_costs: 'description',
        parking_fee: 'description',
        available_date: 'description',
        lease_terms: 'description',
        pets: 'description',
        utilities_included: 'description',
        laundry: 'description',
        parking: 'description',
        heating_cooling: 'description',
        furnished: 'description',
        smoking: 'description',
        amenities: 'description',
        contact_email: 'description',
        address: 'description',
        photos: 'description',
    },
};
/**
 * Fill-report failure kind for a contact email field the site shows but locks to the signed-in
 * account (BR-30). Not a fill failure (BR-22): it opens no operator alert. The single source for
 * the API classifier and the extension; the api-types wire enum mirrors it (asserted at compile time).
 */
export const SIGNED_IN_FAILURE_KIND = 'signed_in';
//# sourceMappingURL=site-config.js.map